import { body1, body2, body3, h3, palette } from '@/modules/defines/styles';
import styled from 'styled-components';
import circleX from '@/assets/icon/circle-x-filled.svg';
import { PropsWithChildren, ReactNode, useState } from 'react';
import ReactStars from 'react-rating-stars-component';

interface TextModalProps {
  title: string;
  isShowCloseButton?: boolean;
  description: PropsWithChildren<ReactNode>;
  type: 'confirm' | 'alert';
  onClose?: () => void;
  confirmText?: string;
  cancelText?: string;
  handleConfirm?: () => void;
  handleCancel?: () => void;
  height?: string;
}

interface ReviewModalProps {
  title: string;

  handleConfirm: (rate: number, comment: string) => void;
  handleSkip: () => void;
}

export const ReviewModal = ({
  title,

  handleConfirm,
  handleSkip,
}: ReviewModalProps) => {
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(5); // 별점 (1-5점)
  return (
    <Layout $height="312px">
      <ReviewLayout>
        <Title>{title}</Title>
        <ReactStars
          value={rating}
          count={5}
          onChange={setRating}
          size={24}
          color="#E5E5EA"
          activeColor="#ffe500"
        />
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="피드백을 입력하세요."
        />
      </ReviewLayout>
      <Textbutton
        type="button"
        onClick={(e) => {
          e.preventDefault();
          handleSkip();
          setComment('');
          setRating(1);
        }}
      >
        다음에 할래요
      </Textbutton>
      <ButtonGroup>
        <Button
          type="button"
          onClick={() => {
            handleConfirm(rating, comment);
          }}
          $color="red"
        >
          제출하기
        </Button>
      </ButtonGroup>
    </Layout>
  );
};

const TextModal = ({
  onClose,
  type,
  title,
  isShowCloseButton = false,
  description,
  handleCancel,
  handleConfirm,
  confirmText = '출차 하기',
  cancelText = '출차 취소',
  height,
}: TextModalProps) => {
  return (
    <Layout $height={height}>
      {isShowCloseButton && (
        <CloseButton
          onClick={onClose}
          width={32}
          height={32}
          src={circleX}
          alt="circle-x"
        />
      )}
      <TextWrapper>
        <Title>{title}</Title>
        <Description>{description}</Description>
      </TextWrapper>
      {type === 'confirm' && (
        <ButtonGroup>
          <Button type="button" onClick={handleConfirm} $color="tertiary">
            {confirmText}
          </Button>
          <Button type="button" onClick={handleCancel} $color="red">
            {cancelText}
          </Button>
        </ButtonGroup>
      )}
      {type === 'alert' && (
        <ButtonGroup>
          <Button type="button" onClick={handleConfirm} $color="red">
            {confirmText}
          </Button>
        </ButtonGroup>
      )}
    </Layout>
  );
};

const CloseButton = styled.img`
  cursor: pointer;
  position: absolute;
  right: 1rem;
  top: -1.5rem;
`;
const Layout = styled.div<{ $height?: string }>`
  position: relative;
  height: ${(props) => props.$height || '11rem'};
  box-sizing: border-box;
`;

const Title = styled.h1`
  ${h3};
  color: #000;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
`;

const Description = styled.div`
  text-align: center;
  ${body2};
  color: ${palette.gray700};
`;
const TextWrapper = styled.div`
  padding: 0 1.37rem;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1rem;
`;

const Button = styled.button<{ $color: 'red' | 'tertiary' }>`
  ${body1};
  width: 100%;
  font-weight: 700;
  height: 3.75rem;
  color: white;
  background: ${(props) => {
    if (props.$color === 'red') {
      return palette.secondaryRed700;
    }

    if (props.$color === 'tertiary') {
      return palette.primaryBlack950;
    }
  }};

  &:active {
    background: ${(props) => {
      if (props.$color === 'red') {
        return palette.primaryRed500;
      }

      if (props.$color === 'tertiary') {
        return palette.gray700;
      }
    }};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  width: 100%;
  position: absolute;
  overflow: hidden;
  bottom: 0px;
  left: 0;
`;

const ReviewLayout = styled.div`
  width: calc(100% - 44px);
  padding-bottom: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  gap: 1rem;

  textarea {
    border-radius: 0.5rem;
    resize: none;
    width: 100%;
    height: 120px;
    padding: 18px 20px 0 20px;
    border: none;
    background: ${palette.gray50};
    ${body2};
    &::placeholder {
      color: ${palette.gray300};
      font-weight: 700;
    }
  }
`;

const Textbutton = styled.button`
  padding: 4px 8px;
  color: ${palette.gray500};
  ${body3};
  text-align: center;
  margin: 0 auto;
  width: fit-content;
  display: block;
`;

export default TextModal;
